import {
    PropsWithChildren,
    startTransition,
    useCallback,
    useMemo,
    useState,
} from "react";
import {
    BillByCounterpartySortValue,
    BillViewOptionGroup,
} from "@common/types/bill";
import { useBillsByCounterparty } from "@api/bill.api";
import { useBillsByCounterpartyPageSortingCache } from "@hooks/view/useBillsByCounterpartyPageSortingCache";
import {
    toBillApiFilters,
    useBillsView,
} from "../../contexts/BillsViewContext";
import { useSortByCounterparty } from "./useSortByCounterparty";
import {
    BillsByCounterpartyTableContext,
    type BillsByCounterpartyTableContextValue,
} from "./BillsByCounterpartyTable.context";

const PER_PAGE = 50;

export const BillsByCounterpartyTableContextProvider: React.FC<
    PropsWithChildren
> = ({ children }) => {
    const [cachedSorting, setCachedSorting] =
        useBillsByCounterpartyPageSortingCache();
    const { filters, viewOption } = useBillsView();
    const [page, setPage] = useState(1);

    const { sortExpression, currentSort, handleSortChange } =
        useSortByCounterparty(cachedSorting);

    const { data, isLoading } = useBillsByCounterparty(
        {
            filters: toBillApiFilters(filters),
            sort: sortExpression,
            page,
            limit: PER_PAGE,
        },
        { enabled: viewOption.groupBy === BillViewOptionGroup.COUNTERPARTY },
    );

    const handleSortChangeWithPageReset = useCallback(
        (value: BillByCounterpartySortValue) => {
            startTransition(() => {
                handleSortChange(value);
                setCachedSorting(value);
                setPage(1);
            });
        },
        [handleSortChange, setCachedSorting, setPage],
    );

    const contextValue = useMemo<BillsByCounterpartyTableContextValue>(
        () => ({
            currentPage: page,
            totalPages: data?.pageCount ?? 1,
            counterpartiesCount: data?.total ?? 0,
            setPage,
            handleSortChange: handleSortChangeWithPageReset,
            currentSort,
            isLoading,
            data: data?.data,
        }),
        [page, data, handleSortChangeWithPageReset, currentSort, isLoading],
    );

    return (
        <BillsByCounterpartyTableContext.Provider value={contextValue}>
            {children}
        </BillsByCounterpartyTableContext.Provider>
    );
};
